// Guided setup: where a seller is, and what to show them next.
//
// Which step a seller sees is worked out from facts we already have — how many marketplace
// accounts they have connected, whether they have any listings, whether one is already live in
// two places — plus the two things their data cannot tell us: whether they still owe setup at all
// (`status`), and whether they actually want to sell on more than one marketplace (`goal`).
//
// Keeping this a plain function means the whole flow can be reasoned about, and tested, without
// rendering anything.

export const SETUP_STATUS = {
  PENDING: 'pending', // a new signup who has not entered setup yet
  ACTIVE: 'active',   // working through it
  DONE: 'done',       // finished
  SKIPPED: 'skipped', // chose to go straight to the app
};

export const SETUP_GOAL = {
  CROSS_LIST: 'cross_list',                 // wants to sell on several marketplaces
  SINGLE_MARKETPLACE: 'single_marketplace', // only manages one, commonly eBay-only
};

export const SETUP_STEP = {
  // Waiting to find out what the seller has, so no step can be chosen yet.
  LOADING: 'loading',
  WELCOME: 'welcome',
  CONNECT: 'connect',
  IMPORT: 'import',
  DONE: 'done',
};

// The order steps are reached in, used to show how far along the seller is. Every step here is one a
// seller can actually reach, so the indicator fills up and finishes — a bar that stops short reads as
// "you did not finish" to someone who is, in fact, done.
export const SETUP_STEP_ORDER = [
  SETUP_STEP.WELCOME,
  SETUP_STEP.CONNECT,
  SETUP_STEP.IMPORT,
];

/**
 * Whether this seller should still be taken through setup.
 * @param {string} status one of SETUP_STATUS
 */
export function isSetupOwed(status) {
  return status === SETUP_STATUS.PENDING || status === SETUP_STATUS.ACTIVE;
}

/**
 * Whether to offer a seller who left setup early the chance to pick it back up.
 *
 * Only while there is actually something left to do. Someone who skipped setup and then connected a
 * marketplace and imported their listings on their own is set up, whatever their status says — and an
 * invitation to finish something already finished is noise that would never stop. This is why the
 * offer needs no dismissal to remember: it retires itself the moment it stops being true.
 *
 * @param {object} progress
 * @param {string} progress.status one of SETUP_STATUS
 * @param {number} progress.connectedAccountCount marketplace accounts connected
 * @param {number} progress.listingCount listings in Voolist
 * @returns {boolean}
 */
export function shouldOfferToFinishSetup ({
  status,
  connectedAccountCount = 0,
  listingCount = 0,
} = {}) {
  if (status !== SETUP_STATUS.SKIPPED) return false;
  return connectedAccountCount === 0 || listingCount === 0;
}

/**
 * The step to show a seller right now.
 *
 * @param {object} progress
 * @param {string} progress.status one of SETUP_STATUS
 * @param {string|null} progress.goal one of SETUP_GOAL, or null before they answer
 * @param {boolean} progress.hasMarketplaceData whether the seller's marketplaces have been read
 *   yet. Until they have, "no accounts connected" and "we have not looked" are the same thing, and
 *   guessing sends a seller who just connected back to the connect step.
 * @param {number} progress.connectedAccountCount connected marketplace accounts
 * @param {boolean} progress.hasAccountAwaitingImport whether a connected account still has an
 *   import the seller has not dealt with. Cannot be derived: an account with no listings looks
 *   identical whether it was never imported or is genuinely empty.
 * @returns {string} one of SETUP_STEP
 */
export function resolveSetupStep({
  status,
  goal = null,
  hasMarketplaceData = false,
  connectedAccountCount = 0,
  hasAccountAwaitingImport = false,
} = {}) {
  if (!isSetupOwed(status)) {
    return SETUP_STEP.DONE;
  }

  // The welcome asks nothing of the seller's data, so it never has to wait.
  if (status === SETUP_STATUS.PENDING) {
    return SETUP_STEP.WELCOME;
  }

  // Every step below is chosen from what the seller has connected, so none of them can be chosen
  // before that is known.
  if (!hasMarketplaceData) {
    return SETUP_STEP.LOADING;
  }

  if (connectedAccountCount === 0) {
    return SETUP_STEP.CONNECT;
  }

  // The import runs on this step and the seller is asked here whether they sell on more than one
  // marketplace. Their answer is what moves them on — not the arrival of listings, because a
  // seller whose marketplace account is genuinely empty must not be stuck waiting for rows.
  if (!goal) {
    return SETUP_STEP.IMPORT;
  }

  if (goal === SETUP_GOAL.SINGLE_MARKETPLACE) {
    return SETUP_STEP.DONE;
  }

  // Cross-listing needs somewhere to cross-list to.
  if (connectedAccountCount < 2) {
    return SETUP_STEP.CONNECT;
  }

  // A marketplace the seller has just added may already hold the same items. Bringing those in is
  // what turns them into one listing Voolist can keep in sync, rather than a second copy.
  if (hasAccountAwaitingImport) {
    return SETUP_STEP.IMPORT;
  }

  // Their marketplaces are connected and their listings are in. Cross-listing happens in the app,
  // where the seller can see the items they are choosing between.
  return SETUP_STEP.DONE;
}
